import * as React from 'react';

import type { W_WorkOrder_GetReviewRecap_VM } from 'ab-api/web/workOrder/getReviewRecap/viewModel';

import type { TimeSheetInfoVM } from 'ab-viewModels/workOrder/submitForReviewRecap';
import type { FieldReportListItemVM } from 'ab-viewModels/workOrder/workOrderFieldReportCard.viewModel';

import type SubmitWorkOrderForReviewRM from 'ab-requestModels/workOrder/submitForReview.requestModel';

import { WorkOrderReviewStatus, WorkOrderReviewLevel } from '@acceligentllc/shared/enums/workOrderReviewStatus';

import ConfirmationModal from 'af-components/ConfirmationModal';

import SubmitForReviewModal from './SubmitForReviewModal';
import SignatureModal from '../../../Shared/SignatureModal';
import SignatureForm from '../../../Shared/SignatureModal/FormModel';
import type { CertificationStatusForm } from '../CertificationStatusModal';
import CertificationStatusModal from '../CertificationStatusModal';
import CompanyPolicyModal from '../CertificationStatusModal/CompanyPolicyModal';
import DigitalSignatureSaveModal from '../DigitalSignatureSaveModal';
import SuccessModalBody from '../Shared/SuccessModalBody';

interface WorkShiftVM {
	accountId: number;
	certificationStatus: Nullable<string>;
	certificationReason: Nullable<string>;
	/** format: ISO_DATETIME */
	startTime: Nullable<string>;
	/** format: ISO_DATETIME */
	endTime: Nullable<string>;
	/** format: ISO_DATETIME */
	outdatedAt: Nullable<string>;
}

interface OwnProps {
	closeModal: () => void;
	currentReviewLevel: WorkOrderReviewLevel;
	currentUserAccountId: number;
	fieldReports: FieldReportListItemVM[];
	isManagerAdminSI: boolean;
	isWorkOrderReadOnly: (reviewLevel: WorkOrderReviewLevel) => boolean;
	onSubmit: (form: SubmitWorkOrderForReviewRM) => Promise<void>;
	showModal: boolean;
	submitToLevel: WorkOrderReviewLevel;
	workOrderId: number;
	workOrderReviewRecap: Nullable<W_WorkOrder_GetReviewRecap_VM>;
	revertOutdated: boolean;
	isCompanyAdmin: boolean;
	isDigitalSignatureEnabled: boolean;
	showCreateDigitalSignature: boolean;
	timezone: Nullable<string>;
	shouldRoundTo1Min: boolean;
	workShifts?: WorkShiftVM[];
}

type Props = OwnProps;

interface State {
	currentUserTimeSheet: Nullable<TimeSheetInfoVM>;
	showSubmitForReviewModal: boolean;
	showSignatureModal: boolean;
	showSuccessModal: boolean;
	showCreateDigitalSignatureModal: boolean;
	showCertificationStatusModal: boolean;
	showCompanyPolicyModal: boolean;
	signatureImage: Nullable<string>;
	certificationStatusData: Nullable<CertificationStatusForm>;
}

class SubmitForReviewModalHandler extends React.PureComponent<Props, State> {

	state: State = {
		currentUserTimeSheet: SubmitForReviewModalHandler.getCurrentUserTimeSheet(
			this.props.currentUserAccountId,
			this.props.workOrderReviewRecap?.timeSheets ?? null
		),
		showSubmitForReviewModal: this.props.showModal,
		showSignatureModal: false,
		showSuccessModal: false,
		showCreateDigitalSignatureModal: false,
		showCertificationStatusModal: false,
		showCompanyPolicyModal: false,
		signatureImage: null,
		certificationStatusData: null,
	};

	static USER_TEXT = 'You are about to sign your time sheet and submit following content for review:';

	static MODAL_TITLE = 'Submit for Review';

	static SUBMIT_TITLE = 'Field Reports Submitted for Review';
	static APPROVE_TITLE = 'Field Reports Approved';

	static REVIEW_DESCRIPTION = 'Report has been successfully submitted for review';
	static APPROVE_DESCRIPTION = 'Report with all its time sheets has been approved and sent for additional review';

	static getCurrentUserTimeSheet = (accountId: number, timeSheets: Nullable<TimeSheetInfoVM[]>) => {
		return timeSheets?.find((_timeSheet) => _timeSheet.accountId === accountId) ?? null;
	};

	static getTitle = (reviewLevel: WorkOrderReviewLevel) => {
		return reviewLevel === WorkOrderReviewLevel.LEVEL_1
			? SubmitForReviewModalHandler.SUBMIT_TITLE
			: SubmitForReviewModalHandler.APPROVE_TITLE;
	};

	static getDescription = (reviewLevel?: WorkOrderReviewLevel) => {
		return reviewLevel === WorkOrderReviewLevel.LEVEL_1
			? SubmitForReviewModalHandler.REVIEW_DESCRIPTION
			: SubmitForReviewModalHandler.APPROVE_DESCRIPTION;
	};

	componentDidUpdate(prevProps: Props) {
		const { showModal } = this.props;

		if ((!prevProps.showModal && showModal) || (prevProps.showModal && !showModal)) {
			this.setState(() => this.stateSetter());
		}
	}

	stateSetter = () => {
		const { showModal, workOrderReviewRecap, currentUserAccountId } = this.props;
		return {
			currentUserTimeSheet: SubmitForReviewModalHandler.getCurrentUserTimeSheet(currentUserAccountId, workOrderReviewRecap?.timeSheets ?? null),
			showSubmitForReviewModal: showModal,
			showSignatureModal: false,
			showSuccessModal: false,
			showCreateDigitalSignatureModal: false,
			showCertificationStatusModal: false,
			showCompanyPolicyModal: false,
			certificationStatusData: null,
		};
	};

	submitForReview = async (form: SignatureForm) => {
		const { onSubmit, submitToLevel, revertOutdated, shouldRoundTo1Min } = this.props;
		const { certificationStatusData } = this.state;

		if (shouldRoundTo1Min) {
			await onSubmit(SignatureForm.toSubmitForReviewRM(form, submitToLevel, revertOutdated, certificationStatusData));
		} else {
			await onSubmit(SignatureForm.toSubmitForReviewRM(form, submitToLevel, revertOutdated));
		}

		// Show success modal
		this.setState(() => ({
			showSubmitForReviewModal: false,
			showSignatureModal: false,
			showSuccessModal: true,
			signatureImage: form.signatureImage,
		}));
	};

	showSignatureModal = () => this.setState(() => ({ showSubmitForReviewModal: false, showSignatureModal: true }));
	showSubmitForReviewModal = () => this.setState(() => ({ showSubmitForReviewModal: true }));
	showCertificationStatusModal = () => this.setState(() => ({ showSubmitForReviewModal: false, showCertificationStatusModal: true }));
	showCompanyPolicyModal = () => this.setState(() => ({ showCertificationStatusModal: false, showCompanyPolicyModal: true }));

	onContinueToSignatureModal = async () => {
		const { shouldRoundTo1Min, workShifts } = this.props;
		const { currentUserTimeSheet } = this.state;

		const hasUnsignedShifts = workShifts?.filter((_ws) => _ws.accountId === currentUserTimeSheet?.accountId)
			.some((_ws) => !!_ws.outdatedAt || !_ws.certificationStatus);

		if (shouldRoundTo1Min && hasUnsignedShifts) {
			this.showCertificationStatusModal();
		} else {
			this.showSignatureModal();
		}
	};

	onSuccessModalConfirm = () => {
		const { closeModal, isDigitalSignatureEnabled, submitToLevel, showCreateDigitalSignature, isCompanyAdmin } = this.props;

		if (!isDigitalSignatureEnabled && !isCompanyAdmin && showCreateDigitalSignature && (submitToLevel > WorkOrderReviewLevel.LEVEL_1)) {
			this.setState(() => ({ showSuccessModal: false, showCreateDigitalSignatureModal: true }));
		} else {
			closeModal();
			this.setState(() => ({ showSuccessModal: false }));
		}
	};

	closeDigitalSignatureModal = () => {
		const { closeModal } = this.props;

		closeModal();
		this.setState(() => ({ showCreateDigitalSignatureModal: false }));
	};

	closeCompanyPolicyModal = () => this.setState(() => ({ showCompanyPolicyModal: false, showCertificationStatusModal: true }));

	closeCertificationStatusModal = () => {
		const { closeModal } = this.props;

		closeModal();
		this.setState(() => ({ showCertificationStatusModal: false }));
	};

	handleCertificationStatusSubmit = (certData: CertificationStatusForm) => {
		this.setState(() => ({
			showCertificationStatusModal: false,
			showSignatureModal: true,
			certificationStatusData: certData,
		}));
	};

	renderSuccessModalBody = () => {
		const { isWorkOrderReadOnly, submitToLevel } = this.props;

		const title = SubmitForReviewModalHandler.getTitle(submitToLevel);
		const description = SubmitForReviewModalHandler.getDescription(submitToLevel);

		return (
			<SuccessModalBody
				description={description}
				isReadOnly={isWorkOrderReadOnly(submitToLevel)}
				reviewStatus={WorkOrderReviewStatus.IN_REVIEW}
				title={title}
			/>
		);
	};

	render = () => {
		const { fieldReports, workOrderReviewRecap, closeModal, submitToLevel, timezone } = this.props;
		const {
			currentUserTimeSheet,
			showSignatureModal,
			showSubmitForReviewModal,
			showSuccessModal,
			showCreateDigitalSignatureModal,
			signatureImage,
			showCertificationStatusModal,
			showCompanyPolicyModal,
		} = this.state;

		const showDigitalSignature = (submitToLevel > WorkOrderReviewLevel.LEVEL_1);

		return (
			<>
				{showSubmitForReviewModal &&
					<SubmitForReviewModal
						closeModal={closeModal}
						currentUserTimeSheet={currentUserTimeSheet}
						fieldReports={fieldReports}
						onConfirm={this.onContinueToSignatureModal}
						showModal={showSubmitForReviewModal}
						timeSheetEntriesByAccountIdMap={workOrderReviewRecap?.timeSheetEntriesByAccountId ?? null}
						timeSheets={workOrderReviewRecap?.timeSheets ?? []}
						timezone={timezone}
					/>}
				{showSignatureModal &&
					<SignatureModal
						closeModal={closeModal}
						description="Submitting a Field Report for review requires a signature."
						onSubmit={this.submitForReview}
						showDigitalSignature={showDigitalSignature}
						showModal={showSignatureModal}
						showNameInput={false}
						title={SubmitForReviewModalHandler.MODAL_TITLE}
					/>}
				<CertificationStatusModal
					closeModal={this.closeCertificationStatusModal}
					onSubmit={this.handleCertificationStatusSubmit}
					openCompanyPolicyModal={this.showCompanyPolicyModal}
					showModal={showCertificationStatusModal}
				/>
				<CompanyPolicyModal
					closeModal={this.closeCompanyPolicyModal}
					showModal={showCompanyPolicyModal}
				/>
				{showSuccessModal &&
					<ConfirmationModal
						body={this.renderSuccessModalBody()}
						closeModal={this.onSuccessModalConfirm}
						confirmAction={this.onSuccessModalConfirm}
						confirmText="Done"
						modalStyle="info"
						showCancel={false}
						showModal={showSuccessModal}
						size="md"
						title={SubmitForReviewModalHandler.MODAL_TITLE}
					/>}
				{showCreateDigitalSignatureModal &&
					<DigitalSignatureSaveModal
						closeModal={this.closeDigitalSignatureModal}
						showModal={showCreateDigitalSignatureModal}
						signatureImage={signatureImage}
					/>}
			</>
		);
	};
}

export default SubmitForReviewModalHandler;
